import { SimpleDialog } from "@powerdiary/components-library/lib";
import { Box } from "@mui/material";
import { useMemo, useState } from "react";
import Confirmation from "./Confirmation";
import {
    AppointmentType,
    AvailabilityTimeModel,
    IdNameItemModel,
    RescheduleAppointmentRequest,
    UpcomingAppointmentDto,
} from "types/generated/models";
import { DateTime } from "luxon";
import { getAvailability, ISlot } from "../../Shared/AvailabilityShared/AvailabilityShared.helpers";
import { useAvailability } from "hooks/useAvailability";
import AvailabilityShared from "../../Shared/AvailabilityShared";
import { BookingType, IAvailabilityData, IProviderData } from "types/appointmentBooking";
import { useAnalytics } from "hooks/useAnalytics";
import { AppointmentRescheduleSteps } from "./index";
import RescheduleDialogActions from "./RescheduleDialogActions";
import { useAppointmentReschedule } from "./useAppointmentReschedule";
import LoadingOverlay from "../../Shared/LoadingOverlay";

interface IAppointmentRescheduleDialogProps {
    handleClose: () => void;
    appointment: UpcomingAppointmentDto | null;
    handleRescheduleSuccess: () => void;
}

export default function AppointmentRescheduleDialog({
    handleClose,
    appointment,
    handleRescheduleSuccess,
}: IAppointmentRescheduleDialogProps) {
    const [currentStep, setCurrentStep] = useState(AppointmentRescheduleSteps.Availability);
    const [currentMonth, setCurrentMonth] = useState(DateTime.now());
    const [selectedDate, setSelectedDate] = useState<DateTime | null>(null);
    const [selectedAvailability, setSelectedAvailability] = useState<IAvailabilityData | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<ISlot | null>(null);
    const [notes, setNotes] = useState("");
    const { track } = useAnalytics();

    const { rescheduleAppointmentAsync, isSaving } = useAppointmentReschedule();

    const { availability: availabilityData, isLoading } = useAvailability({
        locationId: appointment.locationId || 0,
        serviceId: appointment.serviceId || 0,
        providerId: appointment.providerId || 0,
        year: currentMonth.year,
        month: currentMonth.month,
        availabilityType: 1,
        enableTimezones: true,
        clientUtcOffset: DateTime.now().offset,
        appointmentId: appointment?.id,
    });

    const weeks = useMemo(() => {
        return availabilityData ? getAvailability(currentMonth, availabilityData) : [];
    }, [availabilityData, currentMonth]);

    const provider: IProviderData = {
        providerId: appointment?.providerId || 0,
        providerName: appointment?.provider || "",
    };

    const handleOnCancel = () => {
        handleClose();
    };

    const handleOnBack = () => {
        setCurrentStep(AppointmentRescheduleSteps.Availability);
    };

    const handleOnReschedule = async () => {
        const dateTime = DateTime.fromISO(selectedAvailability.timeEntry.dateTimeOffset);
        const dateForAPI = dateTime.toISO({ includeOffset: false });
        const rescheduleAppointmentRequest: RescheduleAppointmentRequest = {
            appointmentId: appointment?.id || 0,
            newDateTime: dateForAPI,
            notes,
            offset: dateTime.offset,
        };

        await rescheduleAppointmentAsync(rescheduleAppointmentRequest);
        handleRescheduleSuccess();
    };

    const handleSlotSelection = (slot: ISlot, selectedProvider?: IdNameItemModel) => {
        setSelectedSlot(slot);

        const timeEntry: AvailabilityTimeModel = {
            ...slot.originalTimeEntry,
            providerId: selectedProvider ? selectedProvider.id : slot.providerId,
        };

        const slotDate = DateTime.fromISO(slot.dateTimeOffset);
        const displayText = `${slotDate.toFormat("MMM dd, yyyy")} at ${slotDate.toFormat("h:mm a")}`;
        const isoDate = slotDate.toISODate();

        if (isoDate) {
            const availabilityData: IAvailabilityData = {
                timeEntry,
                selectedProvider,
                date: isoDate,
                displayText,
            };

            setSelectedAvailability(availabilityData);
            setCurrentStep(AppointmentRescheduleSteps.Confirmation);
            track("booking_reschedule_step_completed", {
                step: "availability",
                booking_type: appointment.appointmentType,
                selected_date: isoDate,
                selected_time: slotDate.toFormat("h:mm a"),
                provider_id: selectedProvider ? selectedProvider.id : slot.providerId,
                provider_name: selectedProvider ? selectedProvider.name : undefined,
            });
        }
    };

    return (
        <>
            {isSaving && <LoadingOverlay />}
            <SimpleDialog
                open
                dialogTitleText="Choose New Appointment Time"
                handleClose={handleClose}
                renderContent={() => (
                    <Box>
                        {currentStep === AppointmentRescheduleSteps.Availability && (
                            <AvailabilityShared
                                weeks={weeks}
                                availabilityData={availabilityData}
                                selectedDate={selectedDate}
                                setSelectedDate={setSelectedDate}
                                selectedSlot={selectedSlot}
                                setSelectedSlot={setSelectedSlot}
                                currentMonth={currentMonth}
                                setCurrentMonth={setCurrentMonth}
                                provider={provider}
                                bookingType={
                                    appointment.appointmentType === AppointmentType.GroupAppointment
                                        ? BookingType.CLASS
                                        : BookingType.CLIENT
                                }
                                handleSlotSelection={handleSlotSelection}
                                isLoading={isLoading}
                            />
                        )}
                        {currentStep === AppointmentRescheduleSteps.Confirmation && (
                            <Confirmation
                                appointment={appointment}
                                selectedAvailability={selectedAvailability}
                                notes={notes}
                                setNotes={setNotes}
                            />
                        )}
                    </Box>
                )}
                renderActions={() => (
                    <RescheduleDialogActions
                        handleOnReschedule={() => void handleOnReschedule()}
                        handleOnBack={handleOnBack}
                        currentStep={currentStep}
                        handleOnCancel={handleOnCancel}
                        isSaving={isSaving}
                    />
                )}
            />
        </>
    );
}
